import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Stack,
  Chip,
  IconButton,
  InputAdornment,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Close as CloseIcon,
  Schedule as ScheduleIcon,
  Build as BuildIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { classificationApi } from '../../services/api';

const EditSLADialog = ({ open, onClose, defectType, userData, onUpdate }) => {
  const [formData, setFormData] = useState({
    temp_fix_sla_hours: '',
    permanent_fix_sla_hours: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(null);

  useEffect(() => {
    if (defectType && open) {
      setFormData({
        temp_fix_sla_hours: defectType.temp_fix_sla_hours || '',
        permanent_fix_sla_hours: defectType.permanent_fix_sla_hours || ''
      });
      setErrors({});
      setApiError(null);
    }
  }, [defectType, open]);

  const validateField = (name, value) => {
    if (value === '' || value === null) {
      return null; // Empty is valid (removes SLA)
    }
    
    const numValue = parseInt(value);
    if (isNaN(numValue) || numValue < 1) {
      return 'Must be a positive integer or empty to remove SLA';
    }
    
    if (numValue > 8760) { // More than a year in hours
      return 'Cannot exceed 8760 hours (1 year)';
    }
    
    return null;
  };

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleInputBlur = (field) => (event) => {
    const value = event.target.value;
    const error = validateField(field, value);
    
    setErrors(prev => ({
      ...prev,
      [field]: error
    }));
  };

  const formatHoursToDisplay = (hours) => {
    if (!hours) return '-';
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
  };

  const isFormValid = () => {
    const tempError = validateField('temp_fix_sla_hours', formData.temp_fix_sla_hours);
    const permError = validateField('permanent_fix_sla_hours', formData.permanent_fix_sla_hours);
    
    return !tempError && !permError;
  };

  const hasChanges = () => {
    const originalTemp = defectType?.temp_fix_sla_hours || '';
    const originalPerm = defectType?.permanent_fix_sla_hours || '';
    
    return formData.temp_fix_sla_hours !== originalTemp.toString() ||
           formData.permanent_fix_sla_hours !== originalPerm.toString();
  };

  const handleSave = async () => {
    if (!isFormValid()) {
      // Validate all fields and show errors
      const tempError = validateField('temp_fix_sla_hours', formData.temp_fix_sla_hours);
      const permError = validateField('permanent_fix_sla_hours', formData.permanent_fix_sla_hours);
      
      setErrors({
        temp_fix_sla_hours: tempError,
        permanent_fix_sla_hours: permError
      });
      return;
    }

    try {
      setLoading(true);
      setApiError(null);

      const updateData = {
        temp_fix_sla_hours: formData.temp_fix_sla_hours === '' ? null : parseInt(formData.temp_fix_sla_hours),
        permanent_fix_sla_hours: formData.permanent_fix_sla_hours === '' ? null : parseInt(formData.permanent_fix_sla_hours)
      };

      await classificationApi.updateSLA(defectType.id, updateData);
      
      // Call the parent component's update callback
      if (onUpdate) {
        onUpdate();
      }
      
      onClose();
    } catch (error) {
      console.error('Error updating SLA settings:', error);
      setApiError(error.response?.data?.error || 'Failed to update SLA settings');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1}>
            <ScheduleIcon color="primary" />
            <Box>
              <Typography variant="h6">
                Edit SLA Settings
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {defectType?.name}
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={handleClose} disabled={loading}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          {/* Info Alert */}
          <Alert severity="info" icon={<InfoIcon />}>
            Set SLA in hours (e.g., 24 for 1 day, 168 for 1 week). Leave empty to remove SLA.
          </Alert>

          {/* Current Values Display */}
          <Box sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>Current SLA Settings:</Typography>
            <Stack direction="row" spacing={2}>
              <Chip 
                icon={<ScheduleIcon />}
                label={`Temp: ${formatHoursToDisplay(defectType?.temp_fix_sla_hours)}`}
                size="small"
                variant="outlined"
              />
              <Chip 
                icon={<BuildIcon />}
                label={`Perm: ${formatHoursToDisplay(defectType?.permanent_fix_sla_hours)}`}
                size="small"
                variant="outlined"
              />
            </Stack>
          </Box>

          {/* Form Fields */}
          <TextField
            label="Temporary Fix SLA Hours"
            value={formData.temp_fix_sla_hours}
            onChange={handleInputChange('temp_fix_sla_hours')}
            onBlur={handleInputBlur('temp_fix_sla_hours')}
            error={!!errors.temp_fix_sla_hours}
            helperText={errors.temp_fix_sla_hours || 'Hours allowed for temporary fix (leave empty to remove)'}
            fullWidth
            type="number"
            inputProps={{ min: 1, max: 8760 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <ScheduleIcon color="action" />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            label="Permanent Fix SLA Hours"
            value={formData.permanent_fix_sla_hours}
            onChange={handleInputChange('permanent_fix_sla_hours')}
            onBlur={handleInputBlur('permanent_fix_sla_hours')}
            error={!!errors.permanent_fix_sla_hours}
            helperText={errors.permanent_fix_sla_hours || 'Hours allowed for permanent fix (leave empty to remove)'}
            fullWidth
            type="number"
            inputProps={{ min: 1, max: 8760 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <BuildIcon color="action" />
                </InputAdornment>
              ),
            }}
          />

          {/* Preview */}
          {(formData.temp_fix_sla_hours || formData.permanent_fix_sla_hours) && (
            <Box sx={{ p: 2, backgroundColor: 'primary.light', color: 'primary.contrastText', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>Preview:</Typography>
              <Stack direction="row" spacing={1}>
                {formData.temp_fix_sla_hours && (
                  <Typography variant="body2">
                    Temp: {formatHoursToDisplay(parseInt(formData.temp_fix_sla_hours))}
                  </Typography>
                )}
                {formData.permanent_fix_sla_hours && (
                  <Typography variant="body2">
                    Perm: {formatHoursToDisplay(parseInt(formData.permanent_fix_sla_hours))}
                  </Typography>
                )}
              </Stack>
            </Box>
          )}

          {/* API Error */}
          {apiError && (
            <Alert severity="error">
              {apiError}
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button 
          onClick={handleClose} 
          disabled={loading}
          color="inherit"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading || !isFormValid() || !hasChanges()}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditSLADialog; 